import { FC, useEffect, useMemo, useState } from 'react'

import {
	Alert,
	Badge,
	Box,
	ColumnLayout,
	Container,
	Flashbar,
	Header,
	Icon,
	Input,
	Link,
	Pagination,
	SpaceBetween,
	Table
} from '@cloudscape-design/components'
import { useNavigate } from 'react-router-dom'

import LoadingSpinner from '../../components/LoadingSpinner'
import useAllAdGroups from '../../hooks/query/useAllAdGroups'
import { useFlashbarStateStore } from '../../stores/notifications/flashbarStateStore'
import { AdGroup } from '../../types/active-directory'

const ACTIVE_DIRECTORY_FLASHBAR_PREFIX = 'active-directory:'
const BROWSEABLE_GROUP_PREFIXES = ['toolchains-app-', 'ssp-app-']
const GROUPS_PAGE_SIZE = 10

const normalizeSearchValue = (value: string): string =>
	value.trim().toLocaleLowerCase()

const buildMembersPagePath = (groupName: string): string =>
	`/active-directory/members?groupName=${encodeURIComponent(groupName)}`

const buildNewMemberPagePath = (groupName: string): string =>
	`/active-directory/new?groupName=${encodeURIComponent(groupName)}`

const ActiveDirectory: FC = () => {
	const navigate = useNavigate()
	const { data: groups, isLoading, error } = useAllAdGroups()
	const { items: flashbarItems } = useFlashbarStateStore()
	const [groupFilter, setGroupFilter] = useState('')
	const [currentPageIndex, setCurrentPageIndex] = useState(1)

	const activeDirectoryFlashbarItems = flashbarItems.filter((item) =>
		item.id.startsWith(ACTIVE_DIRECTORY_FLASHBAR_PREFIX)
	)
	const safeGroups = useMemo(
		() => (Array.isArray(groups) ? groups : []),
		[groups]
	)
	const normalizedFilter = normalizeSearchValue(groupFilter)
	const directoryGroups = useMemo(
		() =>
			[...safeGroups]
				.filter((group) =>
					BROWSEABLE_GROUP_PREFIXES.some((prefix) =>
						normalizeSearchValue(group.name).startsWith(prefix)
					)
				)
				.sort((left, right) => left.name.localeCompare(right.name)),
		[safeGroups]
	)
	const browseableGroups = useMemo(
		() =>
			directoryGroups.filter((group) => {
				if (!normalizedFilter) {
					return true
				}

				return [group.name, group.owner, group.path].some((value) =>
					normalizeSearchValue(value).includes(normalizedFilter)
				)
			}),
		[directoryGroups, normalizedFilter]
	)
	const totalMembers = directoryGroups.reduce(
		(sum, group) => sum + group.members.length,
		0
	)
	const totalOwners = new Set(
		directoryGroups
			.map((group) => normalizeSearchValue(group.owner))
			.filter(Boolean)
	).size
	const pagesCount = Math.max(
		1,
		Math.ceil(browseableGroups.length / GROUPS_PAGE_SIZE)
	)
	const pageStartIndex = (currentPageIndex - 1) * GROUPS_PAGE_SIZE
	const paginatedGroups = browseableGroups.slice(
		pageStartIndex,
		pageStartIndex + GROUPS_PAGE_SIZE
	)
	const visibleGroupCount = paginatedGroups.length

	useEffect(() => {
		setCurrentPageIndex(1)
	}, [normalizedFilter])

	useEffect(() => {
		if (currentPageIndex > pagesCount) {
			setCurrentPageIndex(pagesCount)
		}
	}, [currentPageIndex, pagesCount])

	const tableColumnDefinitions = [
		{
			id: 'name',
			width: 460,
			minWidth: 360,
			header: 'AD Group',
			cell: (group: AdGroup) => (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '1rem'
					}}
				>
					<div
						style={{
							width: '44px',
							height: '44px',
							borderRadius: '14px',
							background: '#edf4ff',
							color: '#1f67ff',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							fontSize: '0.85rem',
							fontWeight: 700
						}}
					>
						AD
					</div>
					<div style={{ minWidth: 0, overflow: 'hidden' }}>
						<Link
							fontSize="heading-s"
							onFollow={() => navigate(buildMembersPagePath(group.name))}
						>
							<span
								style={{
									display: 'block',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis'
								}}
							>
								{group.name}
							</span>
						</Link>
					</div>
				</div>
			)
		},
		{
			id: 'owner',
			width: 320,
			minWidth: 260,
			header: 'Owner',
			cell: (group: AdGroup) => (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '0.5rem',
						color: '#5b7293',
						minWidth: 0,
						overflow: 'hidden'
					}}
				>
					<Icon name="contact" />
					<span
						style={{
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis'
						}}
					>
						{group.owner || 'Not configured'}
					</span>
				</div>
			)
		},
		{
			id: 'memberCount',
			width: 170,
			minWidth: 140,
			header: 'Members',
			cell: (group: AdGroup) => (
				<div style={{ whiteSpace: 'nowrap' }}>
					<Badge color="blue">{`${group.members.length} member${
						group.members.length === 1 ? '' : 's'
					}`}</Badge>
				</div>
			)
		},
		{
			id: 'actions',
			width: 200,
			minWidth: 180,
			header: 'Action',
			cell: (group: AdGroup) => (
				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-start',
						whiteSpace: 'nowrap',
						minWidth: '160px'
					}}
				>
					<button
						type="button"
						onClick={() => navigate(buildNewMemberPagePath(group.name))}
						style={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: '0.45rem',
							background: 'transparent',
							border: 0,
							padding: 0,
							color: '#1f67ff',
							fontSize: '1rem',
							fontWeight: 700,
							cursor: 'pointer',
							whiteSpace: 'nowrap'
						}}
					>
						<Icon name="add-plus" />
						<span>Add Member</span>
					</button>
				</div>
			)
		}
	]

	if (isLoading) return <LoadingSpinner />

	return (
		<SpaceBetween direction="vertical" size="m">
			<>
				{activeDirectoryFlashbarItems.length > 0 && (
					<Flashbar items={activeDirectoryFlashbarItems} />
				)}

				{error && (
					<Alert
						type="error"
						header="Failed to load Active Directory groups"
						statusIconAriaLabel="Error"
					>
						{error instanceof Error ? error.message : 'Unknown error'}
					</Alert>
				)}

				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-end',
						gap: '1rem',
						flexWrap: 'wrap'
					}}
				>
					<div>
						<Header variant="h1">Active Directory Groups</Header>
						<Box color="text-body-secondary">
							Manage permissions, owners, and membership across your
							organization&apos;s AD environment.
						</Box>
					</div>
					<div style={{ width: '420px', maxWidth: '100%' }}>
						<Input
							type="search"
							value={groupFilter}
							placeholder="Search by group or owner..."
							onChange={({ detail }) => setGroupFilter(detail.value)}
						/>
					</div>
				</div>

				<ColumnLayout columns={3} variant="default">
					<Container>
						<Box color="text-label" fontWeight="bold" fontSize="heading-xs">
							TOTAL GROUPS
						</Box>
						<Box variant="h1">{directoryGroups.length}</Box>
					</Container>
					<Container>
						<Box color="text-label" fontWeight="bold" fontSize="heading-xs">
							ACTIVE MEMBERS
						</Box>
						<Box variant="h1">{totalMembers}</Box>
					</Container>
					<Container>
						<Box color="text-label" fontWeight="bold" fontSize="heading-xs">
							TOTAL OWNERS
						</Box>
						<Box variant="h1">{totalOwners}</Box>
					</Container>
				</ColumnLayout>

				<Container>
					<Table
						variant="embedded"
						stickyHeader
						header={<Header variant="h2">Directory</Header>}
						columnDefinitions={tableColumnDefinitions}
						items={paginatedGroups}
						sortingDisabled
						empty={
							<Box textAlign="center" color="inherit">
								<b>No Active Directory groups found</b>
								<Box padding={{ bottom: 's' }} variant="p" color="inherit">
									{normalizedFilter
										? 'Try adjusting the filter and search again.'
										: 'There are no active directory groups available right now.'}
								</Box>
							</Box>
						}
					/>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							gap: '1rem',
							paddingTop: '1rem',
							flexWrap: 'wrap'
						}}
					>
						<Box color="text-body-secondary">
							Showing {visibleGroupCount} of {browseableGroups.length} groups
						</Box>
						<Pagination
							currentPageIndex={currentPageIndex}
							pagesCount={pagesCount}
							onChange={({ detail }) =>
								setCurrentPageIndex(detail.currentPageIndex)
							}
						/>
					</div>
				</Container>
			</>
		</SpaceBetween>
	)
}

export default ActiveDirectory
